import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Drawer,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  createPosHubMenu,
  createPosHubMenuCategory,
  createPosHubMenuItem,
  deletePosHubMenu,
  deletePosHubMenuCategory,
  deletePosHubMenuItem,
  patchPosHubMenuItem,
  getPosHubMenu,
  getPosHubMenuCategories,
  getPosHubMenuItems,
  getPosHubMenuModifierGroups,
  getPosHubMenus,
  getPosHubLocationSettings,
  patchPosHubMenu,
  getAllPosHubCatalogCategories,
  getAllPosHubCatalogProducts,
  syncPosHubMenus,
} from 'api/poshub';
import { captureException } from 'utils/errors';
import { formatLastModified } from 'utils/date';
import { POSHUB_WEEKDAYS } from './constants';
import { hasConfiguredBusinessHours } from './poshubOpeningHours';
import { StyledMenuDrawerBody, StyledMenusTab } from './MenusTab.styles';
import PosHubWeeklyHoursGrid from './PosHubWeeklyHoursGrid';
import { formatPosHubTimeForApi, parsePosHubTimeString } from './poshubTimeFormat';
import { sortMenuItemsForCategory } from './poshubMenuCategorySort';

const WEEKDAY_OPTIONS = POSHUB_WEEKDAYS.map((day) => ({
  value: day,
  label: day.charAt(0) + day.slice(1).toLowerCase(),
}));

const STATUS_COLORS = {
  COMPLETED: 'green',
  ACTIVE: 'green',
  PENDING: 'gold',
  ERROR: 'red',
  NOT_CONNECTED: 'default',
};

/** PosHub menu item payloads vary: categoryId, categoryIds, categories as string[] or { id }[]. */
function getPosHubMenuItemCategoryIds(item) {
  if (!item || typeof item !== 'object') return [];
  const out = [];
  const seen = new Set();
  const add = (v) => {
    if (v == null || v === '') return;
    const s = String(v);
    if (seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  const listish = item.categories ?? item.categoryIds ?? item.category_ids ?? item.menuCategoryIds;
  if (Array.isArray(listish)) {
    listish.forEach((c) => {
      if (c == null) return;
      if (typeof c === 'string' || typeof c === 'number') add(c);
      else if (typeof c === 'object' && c.id != null) add(c.id);
    });
  }
  add(item.categoryId);
  add(item.category_id);
  add(item.menuCategoryId);
  add(item.menu_category_id);
  return out;
}

function posHubItemInMenuCategory(item, menuCategoryId) {
  if (menuCategoryId == null) return false;
  const target = String(menuCategoryId);
  return getPosHubMenuItemCategoryIds(item).some((id) => id === target);
}

/** POS sync often sets catalogCategoryId on items but not menu category UUIDs; menu categories link via catalogCategoryId. */
function getPosHubItemCatalogCategoryId(item) {
  if (!item || typeof item !== 'object') return null;
  return item.catalogCategoryId ?? item.catalog_category_id ?? item.catalogCategory?.id ?? null;
}

function normalizePosHubCategoryName(name) {
  return String(name || '').trim().toLowerCase();
}

/** PosHub uses `showOnline`; when omitted, treat as online (matches Nibble menu sync). */
function getPosHubMenuItemShowOnline(item) {
  if (!item || typeof item !== 'object') return true;
  return item.showOnline !== false;
}

/**
 * PosHub aggregates several POS backends. Menu items may use only flat `modifierGroups` (common for
 * Lightspeed and similar) or a nested `selections` / `selectionGroups` tree (e.g. Toast-style
 * catalogs). Collect unique menu-level group ids from either shape.
 */
function getPosHubMenuItemModifierGroupIds(item) {
  if (!item || typeof item !== 'object') return [];
  const seen = new Set();
  const out = [];

  const add = (id) => {
    if (id == null || id === '') return;
    const s = String(id);
    if (seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };

  const flat = item.modifierGroups;
  if (Array.isArray(flat)) {
    flat.forEach((x) => add(typeof x === 'object' ? x?.id : x));
  }

  const walkGroup = (g) => {
    if (!g || typeof g !== 'object') return;
    add(g.menuModifierGroupId ?? g.menu_modifier_group_id);
    const mods = g.modifiers;
    if (!Array.isArray(mods)) return;
    mods.forEach((m) => {
      if (!m || typeof m !== 'object') return;
      const nest = m.selections;
      if (!Array.isArray(nest)) return;
      nest.forEach((sg) => walkGroup(sg));
    });
  };

  ['selectionGroups', 'selections'].forEach((key) => {
    const block = item[key];
    if (!Array.isArray(block)) return;
    block.forEach((node) => walkGroup(node));
  });

  return out;
}

function getPosHubMenuModifierGroupOptions(g) {
  if (!g || typeof g !== 'object') return [];
  const raw = g.options ?? g.modifiers;
  return Array.isArray(raw) ? raw : [];
}

/** Primary catalog category id on a catalog product (same logic as add-from-catalog). */
function getCatalogProductPrimaryCategoryId(p) {
  if (!p || typeof p !== 'object') return null;
  if (p.categoryId != null) return String(p.categoryId);
  if (Array.isArray(p.categoryIds) && p.categoryIds.length) return String(p.categoryIds[0]);
  if (Array.isArray(p.categories) && p.categories.length) return String(p.categories[0]);
  return null;
}

/** Text for PosHub menu item ``description`` from catalog product fields. */
function getCatalogProductMenuDescription(p) {
  if (!p || typeof p !== 'object') return '';
  const d = p.description != null ? String(p.description).trim() : '';
  if (d) return d;
  const long = p.longDescription != null ? String(p.longDescription).trim() : '';
  return long || '';
}

/**
 * PosHub sometimes returns menu items with `categories: []` (no menu-category ids). In that case, infer placement
 * from catalog: product → catalog category name → menu category with the same name (e.g. Wines, Coffee).
 */
function posHubItemBelongsToMenuCategory(item, cat, catalogCategories, catalogProducts) {
  if (!cat) return false;
  if (posHubItemInMenuCategory(item, cat.id)) return true;
  const itemCc = getPosHubItemCatalogCategoryId(item);
  const catCc = cat.catalogCategoryId ?? cat.catalog_category_id ?? cat.catalogCategory?.id;
  if (itemCc != null && catCc != null && String(itemCc) === String(catCc)) return true;

  const products = catalogProducts || [];
  const categories = catalogCategories || [];
  if (!products.length || !categories.length) return false;
  const pid = item.catalogProductId ?? item.catalogProduct?.id;
  if (!pid) return false;
  const product = products.find((p) => p.id === pid);
  if (!product) return false;
  const catalogCatId = getCatalogProductPrimaryCategoryId(product);
  if (!catalogCatId) return false;
  const catalogCat = categories.find((c) => String(c.id) === catalogCatId);
  const catalogName = catalogCat?.name;
  if (!catalogName || !cat.name) return false;
  return normalizePosHubCategoryName(catalogName) === normalizePosHubCategoryName(cat.name);
}

const makeDefaultWeeklyAvailability = (allOpen = false) => POSHUB_WEEKDAYS.reduce((acc, day) => {
  acc[day] = allOpen
    ? { closed: false, timePeriods: [{ startTime: '', endTime: '' }] }
    : { closed: true, timePeriods: [] };
  return acc;
}, {});

function parseMenuAvailabilityToWeekly(menu) {
  const periods = menu?.availability?.delivery || menu?.availability?.collection || menu?.serviceAvailability || [];
  if (!Array.isArray(periods) || periods.length === 0) {
    return makeDefaultWeeklyAvailability(false);
  }
  const byDay = periods.reduce((acc, p) => {
    if (p?.weekday) acc[p.weekday] = p;
    return acc;
  }, {});
  return POSHUB_WEEKDAYS.reduce((acc, day) => {
    const p = byDay[day];
    const timePeriods = Array.isArray(p?.timePeriods) ? p.timePeriods : [];
    const valid = timePeriods
      .filter((tp) => tp?.startTime && tp?.endTime)
      .map((tp) => ({
        startTime: formatPosHubTimeForApi(parsePosHubTimeString(tp.startTime)) || tp.startTime,
        endTime: formatPosHubTimeForApi(parsePosHubTimeString(tp.endTime)) || tp.endTime,
      }));
    if (valid.length > 0) {
      acc[day] = { closed: false, timePeriods: valid };
    } else {
      acc[day] = { closed: true, timePeriods: [] };
    }
    return acc;
  }, {});
}

function MenusTab({ restaurantId }) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuDetail, setMenuDetail] = useState(null);
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuError, setMenuError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedCatalogCategoryId, setSelectedCatalogCategoryId] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const [customAvailabilityEnabled, setCustomAvailabilityEnabled] = useState(false);
  const [weeklyAvailability, setWeeklyAvailability] = useState(makeDefaultWeeklyAvailability);
  const [copyFromDayCreate, setCopyFromDayCreate] = useState(null);

  const [editCustomAvailabilityEnabled, setEditCustomAvailabilityEnabled] = useState(false);
  const [editWeeklyAvailability, setEditWeeklyAvailability] = useState(makeDefaultWeeklyAvailability);
  const [copyFromDayEdit, setCopyFromDayEdit] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [itemDeleteLoading, setItemDeleteLoading] = useState(null);
  const [itemAvailabilityLoading, setItemAvailabilityLoading] = useState(null);
  const [categoryDeleteLoading, setCategoryDeleteLoading] = useState(null);
  const [openingHoursLoading, setOpeningHoursLoading] = useState(true);
  const [defaultOpeningHoursConfigured, setDefaultOpeningHoursConfigured] = useState(false);
  /** Loaded with menu drawer: used to group items when PosHub returns empty item.categories. */
  const [drawerCatalogCategories, setDrawerCatalogCategories] = useState([]);
  const [drawerCatalogProducts, setDrawerCatalogProducts] = useState([]);
  /** Menu-level modifier groups, keyed by id for fast lookup. */
  const [drawerModifierGroups, setDrawerModifierGroups] = useState([]);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const { categoriesWithItems, uncategorizedItems } = useMemo(() => {
    const withItems = menuCategories.filter((cat) =>
      menuItems.some((item) => posHubItemBelongsToMenuCategory(
        item,
        cat,
        drawerCatalogCategories,
        drawerCatalogProducts,
      )),
    );
    const uncategorized = menuItems.filter(
      (item) => !menuCategories.some((cat) => posHubItemBelongsToMenuCategory(
        item,
        cat,
        drawerCatalogCategories,
        drawerCatalogProducts,
      )),
    );
    return { categoriesWithItems: withItems, uncategorizedItems: uncategorized };
  }, [menuCategories, menuItems, drawerCatalogCategories, drawerCatalogProducts]);

  const refreshMenus = () => {
    if (!restaurantId) return;
    setLoading(true);
    setError(null);
    getPosHubMenus(restaurantId)
      .then((res) => setMenus(res.data || []))
      .catch((e) => {
        captureException(e);
        setError(e?.response?.data?.detail || 'Could not load menus. Make sure this restaurant is connected to the POS integration.');
        setMenus([]);
      })
      .finally(() => setLoading(false));
  };

  /** Queue Celery PosHub → Nibble menu sync so local DB matches PosHub after console edits. */
  const queueNibbleMenuSync = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const data = await syncPosHubMenus(restaurantId);
      message.success(data?.message || 'Nibble menu sync has been queued.');
    } catch (e) {
      captureException(e);
      message.warning(
        e?.response?.data?.detail
          || e?.response?.data?.error
          || 'The integration was updated, but Nibble menu sync could not be queued. Run sync from the POS integration card on the merchant page.',
      );
    }
  }, [restaurantId]);

  useEffect(() => {
    refreshMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) return;
    setOpeningHoursLoading(true);
    getPosHubLocationSettings(restaurantId)
      .then((data) => {
        const hours = data?.data?.businessHours || data?.businessHours || [];
        setDefaultOpeningHoursConfigured(hasConfiguredBusinessHours(hours));
      })
      .catch(() => {
        setDefaultOpeningHoursConfigured(false);
      })
      .finally(() => setOpeningHoursLoading(false));
  }, [restaurantId]);

  const openMenuDrawer = async (menuId) => {
    setSelectedMenuId(menuId);
    setDrawerOpen(true);
    setEditMode(true);
    setMenuError(null);
    setMenuDetail(null);
    setMenuCategories([]);
    setMenuItems([]);
    setDrawerCatalogCategories([]);
    setDrawerCatalogProducts([]);
    setDrawerModifierGroups([]);
    setMenuLoading(true);
    try {
      const [menuRes, catsRes, itemsRes] = await Promise.all([
        getPosHubMenu(restaurantId, menuId),
        getPosHubMenuCategories(restaurantId, menuId),
        getPosHubMenuItems(restaurantId, menuId),
      ]);
      const menu = menuRes?.data ?? menuRes;
      setMenuDetail(menu);
      setMenuCategories(catsRes?.data ?? catsRes ?? []);
      setMenuItems(itemsRes?.data ?? itemsRes ?? []);
      try {
        const [ccRes, cpRes, mgRes] = await Promise.all([
          getAllPosHubCatalogCategories(restaurantId),
          getAllPosHubCatalogProducts(restaurantId),
          getPosHubMenuModifierGroups(restaurantId, menuId),
        ]);
        setDrawerCatalogCategories(ccRes?.data ?? ccRes ?? []);
        setDrawerCatalogProducts(cpRes?.data ?? cpRes ?? []);
        setDrawerModifierGroups(mgRes?.data ?? mgRes ?? []);
      } catch (catalogErr) {
        captureException(catalogErr);
        setMenuError(
          catalogErr?.response?.data?.detail
            || 'Could not load POS catalog. Sync catalog from the Apps tab, then try again.',
        );
      }
      editForm.setFieldsValue({
        name: menu?.name ?? '',
        description: menu?.description ?? '',
        catalogSyncMode: menu?.catalogSyncMode ?? 'MAPPED_ONLY',
      });
      const hasAvailability = (menu?.availability?.delivery?.length > 0)
        || (menu?.availability?.collection?.length > 0)
        || (Array.isArray(menu?.serviceAvailability) && menu.serviceAvailability.length > 0);
      setEditCustomAvailabilityEnabled(!!hasAvailability);
      setEditWeeklyAvailability(parseMenuAvailabilityToWeekly(menu));
    } catch (e) {
      captureException(e);
      setMenuError(e?.response?.data?.detail || 'Could not load menu details.');
    } finally {
      setMenuLoading(false);
    }
  };

  const openAddFromCatalog = async () => {
    if (!restaurantId || !selectedMenuId) return;
    setAddOpen(true);
    setAddLoading(true);
    setSelectedCatalogCategoryId(null);
    setSelectedProductIds([]);
    try {
      const [catsRes, productsRes] = await Promise.all([
        getAllPosHubCatalogCategories(restaurantId),
        getAllPosHubCatalogProducts(restaurantId),
      ]);
      setCatalogCategories(catsRes.data || []);
      setCatalogProducts(productsRes.data || []);
    } catch (e) {
      captureException(e);
      setMenuError(
        e?.response?.data?.detail
          || 'Could not load POS catalog. Sync catalog from the Apps tab, then try again.',
      );
      setAddOpen(false);
    } finally {
      setAddLoading(false);
    }
  };

  const catalogProductsForCategory = selectedCatalogCategoryId
    ? catalogProducts.filter((p) => {
      const cid = selectedCatalogCategoryId;
      return (
        p.categoryId === cid
        || (Array.isArray(p.categoryIds) && p.categoryIds.includes(cid))
        || (Array.isArray(p.categories) && p.categories.includes(cid))
      );
    })
    : [];

  const getProductPrice = (p) => {
    if (typeof p.price === 'number') return p.price;
    if (typeof p.priceCents === 'number') return p.priceCents;
    if (p.price && typeof p.price.amount === 'number') return p.price.amount;
    if (p.price && typeof p.price.value === 'number') return p.price.value;
    return 0;
  };

  const formatProductPriceDisplay = (p) => {
    const raw = getProductPrice(p);
    const dollars = raw / 100;
    return `$${Number(dollars).toFixed(2)}`;
  };

  /** Catalog product IDs already in the current menu (so we don't show them as addable again). */
  const alreadyInMenuCatalogIds = React.useMemo(
    () => new Set(
      (menuItems || [])
        .map((i) => i.catalogProductId ?? i.catalogProduct?.id)
        .filter(Boolean),
    ),
    [menuItems],
  );

  const handleRemoveMenuItem = async (itemId, itemName) => {
    if (!restaurantId || !selectedMenuId) return;
    setItemDeleteLoading(itemId);
    setMenuError(null);
    try {
      await deletePosHubMenuItem(restaurantId, selectedMenuId, itemId);
      const itemsRes = await getPosHubMenuItems(restaurantId, selectedMenuId);
      setMenuItems(itemsRes?.data ?? itemsRes ?? []);
      await queueNibbleMenuSync();
    } catch (e) {
      captureException(e);
      setMenuError(e?.response?.data?.detail || `Failed to remove "${itemName}" from menu.`);
    } finally {
      setItemDeleteLoading(null);
    }
  };

  const handleMenuItemShowOnlineChange = async (itemId, showOnline, itemName) => {
    if (!restaurantId || !selectedMenuId || !itemId) return;
    const row = menuItems.find((i) => i.id === itemId);
    if (row && getPosHubMenuItemShowOnline(row) === showOnline) return;
    setItemAvailabilityLoading(itemId);
    setMenuError(null);
    try {
      await patchPosHubMenuItem(restaurantId, selectedMenuId, itemId, { showOnline });
      const itemsRes = await getPosHubMenuItems(restaurantId, selectedMenuId);
      setMenuItems(itemsRes?.data ?? itemsRes ?? []);
      await queueNibbleMenuSync();
    } catch (e) {
      captureException(e);
      setMenuError(
        e?.response?.data?.detail
        || `Failed to update availability for "${itemName || 'item'}".`,
      );
    } finally {
      setItemAvailabilityLoading(null);
    }
  };

  const handleDeleteMenuCategory = async (categoryId, categoryName, itemsInCategory) => {
    if (!restaurantId || !selectedMenuId) return;
    setCategoryDeleteLoading(categoryId);
    setMenuError(null);
    const refreshMenuStructure = async () => {
      const [catsRes, itemsRes] = await Promise.all([
        getPosHubMenuCategories(restaurantId, selectedMenuId),
        getPosHubMenuItems(restaurantId, selectedMenuId),
      ]);
      setMenuCategories(catsRes?.data ?? catsRes ?? []);
      setMenuItems(itemsRes?.data ?? itemsRes ?? []);
      await queueNibbleMenuSync();
    };
    try {
      try {
        await deletePosHubMenuCategory(restaurantId, selectedMenuId, categoryId);
      } catch (firstErr) {
        const status = firstErr?.response?.status;
        const rows = itemsInCategory || [];
        if (status === 400 && rows.length) {
          for (const row of rows) {
            if (row?.id) {
              await deletePosHubMenuItem(restaurantId, selectedMenuId, row.id);
            }
          }
          await deletePosHubMenuCategory(restaurantId, selectedMenuId, categoryId);
        } else {
          throw firstErr;
        }
      }
      await refreshMenuStructure();
    } catch (e) {
      captureException(e);
      setMenuError(
        e?.response?.data?.detail
          || `Failed to delete category "${categoryName}".`,
      );
    } finally {
      setCategoryDeleteLoading(null);
    }
  };

  const resolveDrawerModifierGroup = (menuModifierGroupId) => {
    const id = menuModifierGroupId == null ? '' : String(menuModifierGroupId);
    return drawerModifierGroups.find((g) => String(g.id) === id) ?? { id, name: id };
  };

  /** Modifier rows from flat `modifierGroups` only (legacy). */
  const getItemModifierGroupsFlat = (item) => {
    const groupIds = getPosHubMenuItemModifierGroupIds(item);
    if (groupIds.length === 0) return [];
    return groupIds.map((id) => resolveDrawerModifierGroup(id)).filter(Boolean);
  };

  const modifierOptionLabel = (mod) => {
    if (!mod || typeof mod !== 'object') return String(mod ?? '');
    if (mod.name) return String(mod.name);
    const mid = mod.menuModifierId ?? mod.id;
    const cat = mod.catalogModifierId;
    const g = drawerModifierGroups.find((grp) => {
      const opts = getPosHubMenuModifierGroupOptions(grp);
      return opts.some((o) => {
        if (!o || typeof o !== 'object') return false;
        return String(o.id) === String(mid) || String(o.menuModifierId) === String(mid);
      });
    });
    if (g) {
      const opts = getPosHubMenuModifierGroupOptions(g);
      const found = opts.find((o) => o && typeof o === 'object'
        && (String(o.id) === String(mid) || String(o.menuModifierId) === String(mid)));
      if (found?.name) return String(found.name);
    }
    if (mid) return String(mid);
    if (cat) return String(cat);
    return 'Option';
  };

  const renderSelectionsModifierTree = (nodes, depth = 0) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    return (
      <div style={{ marginLeft: depth > 0 ? 12 : 0 }}>
        {nodes.map((sel, idx) => {
          if (!sel || typeof sel !== 'object') return null;
          const gid = sel.menuModifierGroupId ?? sel.menu_modifier_group_id;
          const gMeta = resolveDrawerModifierGroup(gid);
          const mods = Array.isArray(sel.modifiers) ? sel.modifiers : [];
          return (
            <div
              key={`${String(gid)}-${idx}`}
              style={{
                marginBottom: 10,
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '8px 12px',
                background: depth > 0 ? '#f5f5f5' : '#fafafa',
                minWidth: 160,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                {gMeta.name || gMeta.id}
                {depth > 0 && (
                  <Tag style={{ marginLeft: 6, fontSize: 11 }} color="geekblue">nested</Tag>
                )}
              </div>
              {mods.map((mod, j) => {
                if (!mod || typeof mod !== 'object') return null;
                const label = modifierOptionLabel(mod);
                const nested = mod.selections ?? mod.selectionGroups;
                return (
                  <div key={j} style={{ marginTop: 6 }}>
                    <Tag style={{ margin: 0, fontSize: 12 }}>{label}</Tag>
                    {Array.isArray(nested) && nested.length > 0
                      ? renderSelectionsModifierTree(nested, depth + 1)
                      : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const menuItemExpandedRowRender = (record) => {
    const groupIds = getPosHubMenuItemModifierGroupIds(record);
    const selectionBlock = (Array.isArray(record?.selections) && record.selections.length > 0)
      ? record.selections
      : (Array.isArray(record?.selectionGroups) ? record.selectionGroups : []);
    const hasSelectionsTree = Array.isArray(selectionBlock) && selectionBlock.length > 0;

    if (groupIds.length === 0) {
      return (
        <div style={{ padding: '8px 16px', color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
          No modifier groups assigned to this item.
        </div>
      );
    }

    if (hasSelectionsTree) {
      return (
        <div style={{ padding: '8px 16px 4px' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
            Modifier groups ({groupIds.length}
            {groupIds.length !== 1 ? ', including nested' : ''}
            )
          </div>
          {renderSelectionsModifierTree(selectionBlock)}
        </div>
      );
    }

    const groups = getItemModifierGroupsFlat(record);
    return (
      <div style={{ padding: '8px 16px 4px' }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
          Modifier Groups ({groups.length})
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {groups.map((g) => {
            const opts = getPosHubMenuModifierGroupOptions(g);
            return (
              <div
                key={g.id}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: '6px 12px',
                  background: '#fafafa',
                  minWidth: 140,
                }}
              >
                <div style={{ fontWeight: 500, fontSize: 13 }}>{g.name || g.id}</div>
                {opts.length > 0 && (
                  <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {opts.map((opt) => {
                      const optName = typeof opt === 'object' ? opt.name : opt;
                      const optId = typeof opt === 'object' ? (opt.id ?? opt.menuModifierId) : opt;
                      return (
                        <Tag key={optId} style={{ margin: 0, fontSize: 12 }}>{optName}</Tag>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const menuItemColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Price', key: 'price', width: 100, render: (_, r) => formatProductPriceDisplay(r) },
    {
      title: 'Availability',
      key: 'showOnline',
      width: 150,
      render: (_, record) => {
        const online = getPosHubMenuItemShowOnline(record);
        return (
          <Tooltip title="You can change the items availability on POS.">
            <Select
              size="small"
              className="poshub-menu-item-availability-select"
              style={{ minWidth: 130 }}
              value={online}
              loading={itemAvailabilityLoading === record.id}
              disabled
              aria-readonly="true"
              onChange={(v) => handleMenuItemShowOnlineChange(record.id, v, record.name)}
              options={[
                {
                  value: true,
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#52c41a',
                          flexShrink: 0,
                        }}
                      />
                      Available
                    </span>
                  ),
                },
                {
                  value: false,
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#8c8c8c',
                          flexShrink: 0,
                        }}
                      />
                      Unavailable
                    </span>
                  ),
                },
              ]}
              labelRender={({ value }) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: value ? '#52c41a' : '#8c8c8c',
                      flexShrink: 0,
                    }}
                  />
                  {value ? 'Available' : 'Unavailable'}
                </span>
              )}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Modifier Groups',
      key: 'modifierGroups',
      width: 130,
      render: (_, r) => {
        const count = getPosHubMenuItemModifierGroupIds(r).length;
        return count > 0
          ? <Tag color="blue">{count} group{count !== 1 ? 's' : ''}</Tag>
          : <span style={{ color: 'rgba(0,0,0,0.35)', fontSize: 12 }}>None</span>;
      },
    },
    { title: 'Updated', key: 'updatedAt', width: 120, render: (_, r) => formatLastModified(r.updatedAt) },
    {
      title: 'Actions',
      key: 'itemActions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Remove from menu"
          description={`Remove "${record.name}" from this menu?`}
          onConfirm={() => handleRemoveMenuItem(record.id, record.name)}
          okText="Remove"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Tooltip title="Remove from menu">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={itemDeleteLoading === record.id}
              disabled={!!itemDeleteLoading || !!itemAvailabilityLoading}
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const handleAddItems = async () => {
    if (!selectedMenuId || selectedProductIds.length === 0) return;
    setAddLoading(true);
    setMenuError(null);
    try {
      const products = catalogProducts.filter((p) => selectedProductIds.includes(p.id));
      const categoryIdToMenuCategoryId = {};

      let nextMenuCategoryPosition = menuCategories.reduce(
        (acc, c) => Math.max(acc, typeof c.position === 'number' ? c.position : 0),
        0,
      );

      /** Per menu-category id: next ``position`` for new items (existing count + batch offset). */
      const nextItemSlotByMenuCategoryId = {};
      const nextMenuItemPosition = (menuCategoryId) => {
        const key = String(menuCategoryId);
        const existingInCategory = menuItems.filter((i) => posHubItemInMenuCategory(i, key)).length;
        const pending = nextItemSlotByMenuCategoryId[key] ?? 0;
        nextItemSlotByMenuCategoryId[key] = pending + 1;
        return existingInCategory + pending + 1;
      };

      const ensureMenuCategory = async (catalogCategoryId) => {
        if (categoryIdToMenuCategoryId[catalogCategoryId]) {
          return categoryIdToMenuCategoryId[catalogCategoryId];
        }
        const catalogCat = catalogCategories.find((c) => String(c.id) === String(catalogCategoryId));
        const catName = catalogCat?.name || 'Uncategorized';
        const existing = menuCategories.find((c) => c.name === catName);
        if (existing) {
          categoryIdToMenuCategoryId[catalogCategoryId] = existing.id;
          return existing.id;
        }
        nextMenuCategoryPosition += 1;
        const createRes = await createPosHubMenuCategory(restaurantId, selectedMenuId, {
          name: catName,
          showOnline: true,
          description: catalogCat?.description != null ? String(catalogCat.description) : '',
          catalogCategoryId,
          position: nextMenuCategoryPosition,
        });
        const created = createRes?.data ?? createRes;
        const menuCategoryId = created?.id;
        categoryIdToMenuCategoryId[catalogCategoryId] = menuCategoryId;
        return menuCategoryId;
      };

      const fallbackCatalogCatId = catalogCategories[0]?.id;

      // Track ids within this batch so mid-loop re-adds are skipped even before menuItems refresh.
      const seenCatalogIds = new Set(alreadyInMenuCatalogIds);

      // eslint-disable-next-line no-restricted-syntax
      for (const p of products) {
        // Server also enforces catalogProductId idempotency; skip here to avoid useless POSTs.
        if (seenCatalogIds.has(p.id)) {
          continue;
        }
        const catalogCatId = getCatalogProductPrimaryCategoryId(p) || fallbackCatalogCatId;
        let menuCategoryId = menuCategories[0]?.id;
        if (catalogCatId) {
          menuCategoryId = await ensureMenuCategory(catalogCatId);
        } else if (fallbackCatalogCatId) {
          menuCategoryId = await ensureMenuCategory(fallbackCatalogCatId);
        }
        if (!menuCategoryId) continue;
        await createPosHubMenuItem(restaurantId, selectedMenuId, {
          catalogProductId: p.id,
          name: p.name,
          price: getProductPrice(p),
          description: getCatalogProductMenuDescription(p),
          categories: [menuCategoryId],
          showOnline: true,
          modifierGroups: [],
          type: 'PRODUCT',
          position: nextMenuItemPosition(menuCategoryId),
        });
        seenCatalogIds.add(p.id);
      }

      const [catsRes, itemsRes] = await Promise.all([
        getPosHubMenuCategories(restaurantId, selectedMenuId),
        getPosHubMenuItems(restaurantId, selectedMenuId),
      ]);
      setMenuCategories(catsRes?.data ?? catsRes ?? []);
      setMenuItems(itemsRes?.data ?? itemsRes ?? []);
      setAddOpen(false);
      await queueNibbleMenuSync();
    } catch (e) {
      captureException(e);
      setMenuError(e?.response?.data?.detail || 'Failed to add items to menu.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      setError(null);
      const payload = {
        name: values.name,
        description: values.description != null ? String(values.description).trim() : '',
        fulfillmentTypes: ['DELIVERY', 'PICKUP', 'DINE-IN'],
        catalogSyncMode: values.catalogSyncMode || 'MAPPED_ONLY',
      };

      if (customAvailabilityEnabled) {
        const periods = POSHUB_WEEKDAYS.flatMap((day) => {
          const config = weeklyAvailability[day];
          if (!config || config.closed || !Array.isArray(config.timePeriods) || !config.timePeriods.length) return [];
          const valid = config.timePeriods.filter((p) => p?.startTime && p?.endTime);
          if (!valid.length) return [];
          return [{ weekday: day, timePeriods: valid }];
        });

        if (periods.length > 0) {
          payload.availability = {
            delivery: periods,
            collection: periods,
          };
        }
        // If custom availability enabled but no periods, fall through to inherit from location
      }
      // Without custom availability, omit the field so PosHub inherits the location's default hours.
      // Setting availability: {delivery:[], collection:[]} closes the menu and breaks order injection.
      await createPosHubMenu(restaurantId, payload);
      setCreateOpen(false);
      createForm.resetFields();
      setCustomAvailabilityEnabled(false);
      setWeeklyAvailability(makeDefaultWeeklyAvailability());
      refreshMenus();
      await queueNibbleMenuSync();
    } catch (e) {
      if (e?.errorFields) return;
      captureException(e);
      setError(e?.response?.data?.detail || 'Failed to create menu.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedMenuId) return;
    try {
      const values = await editForm.validateFields();
      setSaveLoading(true);
      setMenuError(null);
      const payload = {
        name: values.name,
        description: values.description != null ? String(values.description).trim() : '',
        fulfillmentTypes: menuDetail?.fulfillmentTypes ?? ['DELIVERY', 'PICKUP', 'DINE-IN'],
        catalogSyncMode: values.catalogSyncMode ?? menuDetail?.catalogSyncMode,
      };
      if (editCustomAvailabilityEnabled) {
        const periods = POSHUB_WEEKDAYS.flatMap((day) => {
          const config = editWeeklyAvailability[day];
          if (!config || config.closed || !Array.isArray(config.timePeriods) || !config.timePeriods.length) return [];
          const valid = config.timePeriods.filter((p) => p?.startTime && p?.endTime);
          if (!valid.length) return [];
          return [{ weekday: day, timePeriods: valid }];
        });
        if (periods.length > 0) {
          payload.availability = { delivery: periods, collection: periods };
        }
        // If enabled but no valid periods, fall through and omit field to inherit from location
      }
      // Without custom availability, omit the field so PosHub inherits location's default hours.
      const res = await patchPosHubMenu(restaurantId, selectedMenuId, payload);
      const menu = res?.data ?? res;
      setMenuDetail(menu);
      setEditMode(false);
      refreshMenus();
      setDrawerOpen(false);
      setSelectedMenuId(null);
      await queueNibbleMenuSync();
    } catch (e) {
      if (e?.errorFields) return;
      captureException(e);
      setMenuError(e?.response?.data?.detail || 'Failed to save menu.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (menuId, menuName) => {
    if (!restaurantId) return;
    setDeleteLoading(menuId);
    setError(null);
    try {
      await deletePosHubMenu(restaurantId, menuId);
      if (selectedMenuId === menuId) {
        setDrawerOpen(false);
        setSelectedMenuId(null);
      }
      refreshMenus();
      await queueNibbleMenuSync();
    } catch (e) {
      captureException(e);
      setError(e?.response?.data?.detail || `Failed to delete menu "${menuName}".`);
    } finally {
      setDeleteLoading(null);
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', width: 220 },
    {
      title: 'Last modified',
      key: 'last_modified',
      width: 140,
      render: (_, row) => formatLastModified(row.updatedAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
          <Tooltip title="View / Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openMenuDrawer(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete menu"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => handleDelete(record.id, record.name)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoading === record.id}
                disabled={!!deleteLoading}
              />
            </Tooltip>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <StyledMenusTab>
      <div className="menus-header">
        <Tooltip
          title={
            defaultOpeningHoursConfigured || openingHoursLoading
              ? undefined
              : 'Set default opening hours in the Settings tab before creating a menu.'
          }
        >
          <span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={openingHoursLoading || !defaultOpeningHoursConfigured}
              onClick={() => setCreateOpen(true)}
            >
              Create Menu
            </Button>
          </span>
        </Tooltip>
      </div>
      {!openingHoursLoading && !defaultOpeningHoursConfigured && (
        <Alert
          type="info"
          showIcon
          message="Default opening hours required"
          description="Menus use the location's default hours unless you enable custom availability on a menu. Set default opening hours in the Settings tab before creating a menu."
          style={{ marginBottom: 16 }}
        />
      )}
      {error && (
        <Alert
          type="warning"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        className="poshub-console-table"
        columns={columns}
        dataSource={menus}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'No menus yet. Create a menu from your catalog.' }}
      />

      <Modal
        title="Create menu"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setCustomAvailabilityEnabled(false);
          setWeeklyAvailability(makeDefaultWeeklyAvailability());
        }}
        onOk={handleCreate}
        okText="Create"
        confirmLoading={createLoading}
        destroyOnClose
        width={920}
        centered
        styles={{ body: { maxHeight: 'min(70vh, 720px)', overflowY: 'auto', paddingTop: 8 } }}
      >
        <Form layout="vertical" form={createForm}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Menu name is required' }]}
          >
            <Input placeholder="Menu name" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea
              placeholder="Optional description shown where the integration supports it"
              rows={3}
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item
            label="Catalog Sync Mode"
            name="catalogSyncMode"
            rules={[{ required: true, message: 'Catalog sync mode is required' }]}
          >
            <Select
              placeholder="Select catalog sync mode"
              options={[
                {
                  value: 'MAPPED_ONLY',
                  label: 'Sync future updates only for items in this menu',
                },
                {
                  value: 'ALL',
                  label: 'Sync ALL POS menu updates to marketplaces',
                },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={customAvailabilityEnabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                setCustomAvailabilityEnabled(enabled);
                if (enabled) {
                  setWeeklyAvailability(makeDefaultWeeklyAvailability(true));
                } else {
                  setWeeklyAvailability(makeDefaultWeeklyAvailability(false));
                }
              }}
            >
              Enable custom menu availability
            </Checkbox>
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14, marginTop: 4, lineHeight: 1.5 }}>
              By enabling this, you can set custom opening hours for the menu.
              {' '}
              (Note: By default, the menu inherits opening hours from the location.)
            </div>
          </Form.Item>
          {customAvailabilityEnabled && (
            <div
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 20,
                background: '#fafafa',
                fontSize: 14,
              }}
            >
              <Flex wrap="wrap" gap="small" align="center" style={{ marginBottom: 12 }}>
                <Select
                  placeholder="Select day to copy from"
                  value={copyFromDayCreate}
                  onChange={setCopyFromDayCreate}
                  style={{ minWidth: 200, fontSize: 13 }}
                  size="middle"
                  options={WEEKDAY_OPTIONS}
                />
                <Button
                  size="middle"
                  style={{ fontSize: 13 }}
                  disabled={!copyFromDayCreate}
                  onClick={() => {
                    if (!copyFromDayCreate) return;
                    const source = weeklyAvailability[copyFromDayCreate];
                    const toCopy = (source?.timePeriods || []).filter((p) => p?.startTime && p?.endTime);
                    setWeeklyAvailability((prev) => {
                      const next = { ...prev };
                      POSHUB_WEEKDAYS.forEach((d) => {
                        if (d === copyFromDayCreate) return;
                        next[d] = {
                          closed: source?.closed ?? true,
                          timePeriods: toCopy.length ? toCopy.map((p) => ({ ...p })) : [],
                        };
                      });
                      return next;
                    });
                  }}
                >
                  Copy to all days
                </Button>
              </Flex>
              <PosHubWeeklyHoursGrid
                value={weeklyAvailability}
                onChange={setWeeklyAvailability}
              />
            </div>
          )}
        </Form>
      </Modal>

      <Drawer
        title={menuDetail?.name ? `Menu: ${menuDetail.name}` : 'Menu'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedMenuId(null);
          setEditMode(false);
        }}
        width={900}
        styles={{ body: { paddingBottom: 24 } }}
        destroyOnClose
      >
        <StyledMenuDrawerBody>
        {menuError && (
          <Alert
            type="warning"
            message={menuError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <div className="poshub-drawer-meta-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {menuDetail?.status && (
              <Tag color={STATUS_COLORS[menuDetail.status] ?? 'default'}>{menuDetail.status}</Tag>
            )}
            {menuDetail?.updatedAt && <span>Last modified: {formatLastModified(menuDetail.updatedAt)}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={openAddFromCatalog} disabled={menuLoading}>
              Add from catalog
            </Button>
            {!editMode ? (
              <Button onClick={() => setEditMode(true)} disabled={menuLoading}>
                Edit header
              </Button>
            ) : (
              <>
                <Button onClick={() => setEditMode(false)} disabled={saveLoading}>
                  Cancel
                </Button>
                <Button type="primary" onClick={handleSave} loading={saveLoading}>
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        <Form layout="vertical" form={editForm} disabled={!editMode || menuLoading}>
          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Name</span>}
            name="name"
            rules={[{ required: true, message: 'Menu name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Description</span>}
            name="description"
          >
            <Input.TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Catalog Sync Mode</span>}
            name="catalogSyncMode"
          >
            <Select
              placeholder="Select catalog sync mode"
              options={[
                {
                  value: 'MAPPED_ONLY',
                  label: 'Sync future updates only for items in this menu',
                },
                {
                  value: 'ALL',
                  label: 'Sync ALL POS menu updates to marketplaces',
                },
              ]}
            />
          </Form.Item>
          <div style={{ borderTop: '1px solid #e7e8e9', margin: '4px 0 16px' }} />
          <Form.Item>
            <Checkbox
              checked={editCustomAvailabilityEnabled}
              disabled={!editMode || menuLoading}
              onChange={(e) => {
                const enabled = e.target.checked;
                setEditCustomAvailabilityEnabled(enabled);
                if (enabled) {
                  setEditWeeklyAvailability(makeDefaultWeeklyAvailability(true));
                } else {
                  setEditWeeklyAvailability(makeDefaultWeeklyAvailability(false));
                }
              }}
            >
              Enable custom menu availability
            </Checkbox>
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
              By enabling this, you can set custom opening hours for the menu.
              {' '}
              (Note: By default, the menu inherits opening hours from the location.)
            </div>
          </Form.Item>
          {editCustomAvailabilityEnabled && (
            <div
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 20,
                background: '#fafafa',
                fontSize: 14,
              }}
            >
              <Flex wrap="wrap" gap="small" align="center" style={{ marginBottom: 12 }}>
                <Select
                  placeholder="Select day to copy from"
                  value={copyFromDayEdit}
                  onChange={setCopyFromDayEdit}
                  style={{ minWidth: 200, fontSize: 13 }}
                  size="middle"
                  options={WEEKDAY_OPTIONS}
                  disabled={!editMode || menuLoading}
                />
                <Button
                  size="middle"
                  style={{ fontSize: 13 }}
                  disabled={!editMode || menuLoading || !copyFromDayEdit}
                  onClick={() => {
                    if (!copyFromDayEdit) return;
                    const source = editWeeklyAvailability[copyFromDayEdit];
                    const toCopy = (source?.timePeriods || []).filter((p) => p?.startTime && p?.endTime);
                    setEditWeeklyAvailability((prev) => {
                      const next = { ...prev };
                      POSHUB_WEEKDAYS.forEach((d) => {
                        if (d === copyFromDayEdit) return;
                        next[d] = {
                          closed: source?.closed ?? true,
                          timePeriods: toCopy.length ? toCopy.map((p) => ({ ...p })) : [],
                        };
                      });
                      return next;
                    });
                  }}
                >
                  Copy to all days
                </Button>
              </Flex>
              <PosHubWeeklyHoursGrid
                value={editWeeklyAvailability}
                onChange={setEditWeeklyAvailability}
                disabled={!editMode || menuLoading}
              />
            </div>
          )}
        </Form>

        <h3 className="poshub-menu-items-section-title">Menu items by category</h3>
        {menuLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>
        ) : menuItems.length === 0 ? (
          <div className="poshub-menus-empty">
            No menu items yet. Add items from the catalog to create categories.
          </div>
        ) : (
          <Collapse
            className="poshub-collapse"
            defaultActiveKey={
              categoriesWithItems.length
                ? categoriesWithItems.map((c) => c.id)
                : uncategorizedItems.length
                  ? ['uncategorized']
                  : []
            }
          >
            {categoriesWithItems.map((cat) => {
              const itemsInCategory = sortMenuItemsForCategory(
                menuItems.filter((item) => posHubItemBelongsToMenuCategory(
                  item,
                  cat,
                  drawerCatalogCategories,
                  drawerCatalogProducts,
                )),
                cat,
              );
              return (
                <Collapse.Panel
                  key={cat.id}
                  header={(
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 8,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>
                        {cat.name}
                        <Tag color="default" style={{ marginLeft: 8 }}>
                          {itemsInCategory.length} item{itemsInCategory.length !== 1 ? 's' : ''}
                        </Tag>
                      </span>
                      <span role="presentation" onClick={(e) => e.stopPropagation()}>
                        <Popconfirm
                          title="Delete category"
                          description={`Remove "${cat.name}" and all ${itemsInCategory.length} item${itemsInCategory.length !== 1 ? 's' : ''} from this menu?`}
                          onConfirm={() => handleDeleteMenuCategory(cat.id, cat.name, itemsInCategory)}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                          cancelText="Cancel"
                          disabled={!!categoryDeleteLoading || !!itemDeleteLoading || !!itemAvailabilityLoading}
                        >
                          <Tooltip title="Delete category from menu">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              loading={categoryDeleteLoading === cat.id}
                              disabled={!!categoryDeleteLoading || !!itemDeleteLoading || !!itemAvailabilityLoading}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </span>
                    </div>
                  )}
                >
                  <Table
                    className="poshub-console-table"
                    size="small"
                    rowKey="id"
                    dataSource={itemsInCategory}
                    pagination={false}
                    showHeader
                    columns={menuItemColumns}
                    expandable={{
                      expandedRowRender: menuItemExpandedRowRender,
                      rowExpandable: () => true,
                    }}
                  />
                </Collapse.Panel>
              );
            })}
            {uncategorizedItems.length > 0 && (
              <Collapse.Panel
                key="uncategorized"
                header={(
                  <span style={{ fontWeight: 600 }}>
                    Uncategorized
                    <Tag color="default" style={{ marginLeft: 8 }}>
                      {uncategorizedItems.length} item{uncategorizedItems.length !== 1 ? 's' : ''}
                    </Tag>
                  </span>
                )}
              >
                <Table
                  className="poshub-console-table"
                  size="small"
                  rowKey="id"
                  dataSource={uncategorizedItems}
                  pagination={false}
                  showHeader
                  columns={menuItemColumns}
                  expandable={{
                    expandedRowRender: menuItemExpandedRowRender,
                    rowExpandable: () => true,
                  }}
                />
              </Collapse.Panel>
            )}
          </Collapse>
        )}
        </StyledMenuDrawerBody>
      </Drawer>

      <Modal
        title="Add items from catalog"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={handleAddItems}
        okText="Add items"
        confirmLoading={addLoading}
        okButtonProps={{ disabled: selectedProductIds.length === 0 }}
        width={900}
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: 16, minHeight: 420 }}>
          <div className="poshub-modal-catalog-sidebar" style={{ width: 260, paddingRight: 12 }}>
            <div className="poshub-modal-section-title" style={{ marginBottom: 8 }}>Catalog categories</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {catalogCategories.map((c) => (
                <Button
                  key={c.id}
                  type={selectedCatalogCategoryId === c.id ? 'primary' : 'default'}
                  onClick={() => setSelectedCatalogCategoryId(c.id)}
                  style={{ textAlign: 'left' }}
                  block
                >
                  {c.name}
                </Button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span className="poshub-modal-section-title">Catalog items</span>
              {selectedProductIds.length > 0 && (
                <span style={{ fontSize: 15, fontWeight: 500, color: '#717679' }}>
                  {selectedProductIds.length} item{selectedProductIds.length !== 1 ? 's' : ''} to add
                </span>
              )}
            </div>
            {selectedCatalogCategoryId ? (
              <Table
                className="poshub-console-table"
                size="small"
                rowKey="id"
                dataSource={catalogProductsForCategory}
                pagination={{ pageSize: 10 }}
                rowSelection={{
                  selectedRowKeys: [...new Set([...selectedProductIds, ...Array.from(alreadyInMenuCatalogIds)])],
                  onChange: async (keys) => {
                    const currentCategoryIds = catalogProductsForCategory.map((p) => p.id);
                    const otherCategorySelections = selectedProductIds.filter(
                      (id) => !currentCategoryIds.includes(id),
                    );
                    const keysInThisCategory = keys.filter((id) => currentCategoryIds.includes(id));
                    const newSelectedProductIds = [...otherCategorySelections, ...keysInThisCategory.filter((id) => !alreadyInMenuCatalogIds.has(id))];
                    setSelectedProductIds(newSelectedProductIds);

                    const uncheckedInMenu = currentCategoryIds.filter(
                      (id) => alreadyInMenuCatalogIds.has(id) && !keys.includes(id),
                    );
                    if (uncheckedInMenu.length > 0 && restaurantId && selectedMenuId) {
                      setMenuError(null);
                      for (const catalogProductId of uncheckedInMenu) {
                        const menuItem = menuItems.find(
                          (m) => (m.catalogProductId ?? m.catalogProduct?.id) === catalogProductId,
                        );
                        if (menuItem?.id) {
                          try {
                            await deletePosHubMenuItem(restaurantId, selectedMenuId, menuItem.id);
                          } catch (e) {
                            captureException(e);
                            setMenuError(e?.response?.data?.detail || 'Failed to remove item from menu.');
                          }
                        }
                      }
                      const itemsRes = await getPosHubMenuItems(restaurantId, selectedMenuId);
                      setMenuItems(itemsRes?.data ?? itemsRes ?? []);
                      await queueNibbleMenuSync();
                    }
                  },
                }}
                columns={[
                  { title: 'Name', dataIndex: 'name', key: 'name' },
                  {
                    title: 'Price',
                    key: 'price',
                    width: 120,
                    render: (_, r) => formatProductPriceDisplay(r),
                  },
                ]}
              />
            ) : (
              <Alert type="info" message="Select a category to see items." showIcon />
            )}
          </div>
        </div>
      </Modal>
    </StyledMenusTab>
  );
}

export default MenusTab;
